import React, { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle, Color, FontSize } from "@tiptap/extension-text-style";
import EditorToolbar from "./rich-editor/EditorToolbar";
import SelectionToolbar from "./rich-editor/SelectionToolbar";
import { storageToHtml, htmlToStorage } from "./rich-editor/richEditorUtils";
import "./RichChapterEditor.css";

/**
 * Rich text editor for generated chapter content.
 * Replaces StyledTextEditor — supports bold, italic, font family, font size, and color.
 * Internally converts between our storage format (<b>, <i>, <font name>, etc.) and Tiptap HTML.
 */
const RichChapterEditor = ({ content, onChange, className, style }) => {
  const isInternalUpdate = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable heading/code/blockquote — we only want inline formatting
        heading: false,
        codeBlock: false,
        blockquote: false,
        code: false,
        horizontalRule: false,
      }),
      TextStyle,
      Color,
      FontSize,
    ],
    content: storageToHtml(content),
    onUpdate: ({ editor }) => {
      if (!onChange) return;
      isInternalUpdate.current = true;
      onChange(htmlToStorage(editor.getHTML()));
    },
  });

  // Sync external content changes (e.g. AI-generated content arriving) without resetting cursor
  useEffect(() => {
    if (!editor) return;
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    const incoming = storageToHtml(content);
    // Only update if content actually differs to avoid cursor resets
    if (editor.getHTML() !== incoming) {
      editor.commands.setContent(incoming, false);
    }
  }, [content, editor]);

  return (
    <div className={`rich-chapter-editor ${className || ""}`} style={style}>
      <SelectionToolbar editor={editor} />
      <EditorToolbar editor={editor} />
      <EditorContent
        editor={editor}
        className="rich-chapter-editor__content"
      />
    </div>
  );
};

export default RichChapterEditor;
